import {RuleBundleType} from '@frontapp/workflows/rules';
import React, {FC, PropsWithChildren} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  TimelineActivityActionsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  ActivitySourceContactModel,
  ActivitySourceMacroModel,
  ActivitySourceModel,
  ActivitySourceOtherModel,
  ActivitySourceRuleModel,
  ActivitySourceTeammateModel,
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {TeammateNamePreviewer} from '../../../../teammateCard/teammateCardPreviewer';
import {ActivityRuleOrMacroPill} from '../activityRulePill/activityRuleOrMacroPill';
import {BasicViewerProps} from './basicViewer';

/*
 * Constants.
 */

const helpCenterDataLeakUrl = `${helpCenterUrl}/t/m2bx52/how-smart-merge-works#why-did-my-smart-merged-conversation-split-into-a-new-conversation`;

interface OutOfOfficeActivitySourceProps {
  userId: number;
  sourceTeammate: TeammateModel;
  panelId: ViewerPanelId;
}

interface RuleOrMacroActivitySourceProps {
  panelId: ViewerPanelId;
  activitySource: ActivitySourceRuleModel | ActivitySourceMacroModel;
  timelineActivityAction: TimelineActivityActionsEnum;

  macroRequester: TeammateModel | undefined;
  userId: number | undefined;
}

interface ApiActivitySourceProps {
  activitySource: ActivitySourceModel;
}

/* If the source teammate is out of office, render the display name with additional text about the out of office. */
export const OutOfOfficeActivitySource: FC<PropsWithChildren<OutOfOfficeActivitySourceProps>> = ({
  userId,
  sourceTeammate,
  panelId,
}) => {
  const sourceName = <TeammateNamePreviewer teammate={sourceTeammate} panelId={panelId} />;

  return userId === sourceTeammate.id ? (
    <Trans id="activity-unassigned-teammate-ooo-target-you">because {sourceName} are out of office</Trans>
  ) : (
    <Trans id="activity-unassigned-teammate-ooo-target-they">because {sourceName} is out of office</Trans>
  );
};

/* Render the name of a rule activity source with a link attached. */
export const RuleOrMacroActivitySource: FC<PropsWithChildren<RuleOrMacroActivitySourceProps>> = (props) => {
  const {panelId, activitySource, timelineActivityAction, macroRequester, userId} = props;

  const onClickTrackingCallback = () => {
    if (activitySource.visualType === ActivitySourceVisualTypesEnum.RULE) {
      trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
        action: timelineActivityAction,
        rule_id: activitySource.ruleId,
        rule_bundle_id: activitySource.ruleBundleId,
        rule_bundle_type: activitySource.ruleBundleType,
      });
    } else {
      trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
        action: timelineActivityAction,
        macro_id: activitySource.macroId,
      });
    }
  };

  if (macroRequester && userId) {
    return (
      <Trans id="activity-basic-macro-requester">
        <TeammateNamePreviewer teammate={macroRequester} panelId={panelId} /> with{' '}
        <ActivityRuleOrMacroPill
          panelId={panelId}
          source={activitySource}
          onClickTrackingCallback={onClickTrackingCallback}
        />
      </Trans>
    );
  }

  return (
    <ActivityRuleOrMacroPill
      panelId={panelId}
      source={activitySource}
      onClickTrackingCallback={onClickTrackingCallback}
    />
  );
};

export const ApiActivitySource: FC<PropsWithChildren<ApiActivitySourceProps>> = (props) => {
  const {activitySource} = props;

  if (
    activitySource.type !== ActivitySourceTypesEnum.PUBLIC_API ||
    activitySource.visualType !== ActivitySourceVisualTypesEnum.OTHER
  ) {
    return null;
  }

  return <Trans id="activity-basic-tag-api">API</Trans>;
};

// NOTE: This is being used as a fallback. For the new activities design, using
// the above activity source components to handle the new text arrangements.
export const ActivitySource: FC<PropsWithChildren<BasicViewerProps>> = (props) => {
  const {source} = props.activity;
  switch (source.visualType) {
    case ActivitySourceVisualTypesEnum.TEAMMATE:
      return renderSourceTeammate(props, source);

    case ActivitySourceVisualTypesEnum.RULE:
      return renderSourceRule(source);

    case ActivitySourceVisualTypesEnum.CONTACT:
      return renderSourceContact(source);

    case ActivitySourceVisualTypesEnum.MACRO:
      return renderSourceMacro(source);

    default:
      return renderSourceOther(source);
  }
};

function renderSourceTeammate(props: BasicViewerProps, source: ActivitySourceTeammateModel) {
  // Make sure we have a source teammate.
  const {sourceTeammate} = props;
  if (!sourceTeammate) {
    return null;
  }

  // If the teammate was unavailable, we need a different message.
  if (source.wasUnavailable) {
    return (
      <Trans id="activity-source-teammate-ooo">
        by {buildTeammateDisplayName(sourceTeammate)} (out of office)
      </Trans>
    );
  }

  if (source.wasDeleted) {
    return (
      <Trans id="activity-source-teammate-deleted">
        because {buildTeammateDisplayName(sourceTeammate)} is no longer an active Front user
      </Trans>
    );
  }

  // Otherwise, just use the teammate name.
  return <Trans id="activity-source-teammate">by {buildTeammateDisplayName(sourceTeammate)}</Trans>;
}

function renderSourceRule(source: ActivitySourceRuleModel) {
  return source.ruleBundleType === RuleBundleType.MACRO ? (
    <Trans id="activity-source-rule-macro">by a macro – &quot;{source.ruleName}&quot;</Trans>
  ) : (
    <Trans id="activity-source-rule">by a rule – &quot;{source.ruleName}&quot;</Trans>
  );
}

function renderSourceMacro(source: ActivitySourceMacroModel) {
  return <Trans id="activity-source-macro">by a macro – &quot;{source.macroName}&quot;</Trans>;
}

function renderSourceContact(source: ActivitySourceContactModel) {
  return <Trans id="activity-source-contact">by {source.name}</Trans>;
}

function renderSourceOther(source: ActivitySourceOtherModel) {
  switch (source.type) {
    case ActivitySourceTypesEnum.RULE:
      return <Trans id="activity-source-rulenoname">by a rule</Trans>;

    case ActivitySourceTypesEnum.REMINDER:
      return <Trans id="activity-source-reminder">by a reminder</Trans>;

    case ActivitySourceTypesEnum.INACTIVE:
      return <Trans id="activity-source-inactive">because of inactivity</Trans>;

    case ActivitySourceTypesEnum.PUBLIC_API:
      return <Trans id="activity-source-api">from the API</Trans>;

    case ActivitySourceTypesEnum.SPAM_FILTER:
      return <Trans id="activity-source-spam">by spam filtering</Trans>;

    case ActivitySourceTypesEnum.GMAIL:
      return <Trans id="activity-source-gmail">in the Gmail inbox</Trans>;

    case ActivitySourceTypesEnum.OFFICE365:
      return <Trans id="activity-source-office365">in the Office 365 inbox</Trans>;

    case ActivitySourceTypesEnum.INTERCOM:
      return <Trans id="activity-source-intercom">in Intercom</Trans>;

    case ActivitySourceTypesEnum.GITHUB:
      return <Trans id="activity-source-github">in Github</Trans>;

    case ActivitySourceTypesEnum.SLACK:
      return <Trans id="activity-source-slack">in Slack</Trans>;

    case ActivitySourceTypesEnum.CONVERSATION_SIZE:
      return <Trans id="activity-source-conversationsize">due to its size</Trans>;

    case ActivitySourceTypesEnum.DATA_LEAK:
      return (
        <Trans id="activity-source-dataleak">
          for privacy reasons.{' '}
          <InlineLinkButton onClick={onDataLeakLearnMoreClick}>Learn more</InlineLinkButton>.
        </Trans>
      );

    case ActivitySourceTypesEnum.COMPANY:
    case ActivitySourceTypesEnum.SENDING:
      return null;

    default:
      return <Trans id="activity-source-name">by {source.name}</Trans>;
  }
}

/*
 * Helpers.
 */

function onDataLeakLearnMoreClick() {
  openInBrowser(helpCenterDataLeakUrl);
}
